import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'mariani_auth_token';
const USER_KEY = 'mariani_user';

// Set/clear axios global Authorization header
function setAxiosAuthHeader(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

// Initialize at module load so first API calls already include the header
setAxiosAuthHeader(localStorage.getItem(TOKEN_KEY));

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [accounts, setAccounts] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [categoryCount, setCategoryCount] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [hasReplied, setHasReplied] = useState(() =>
    localStorage.getItem('mariani_has_replied') === 'true'
  );
  const [loading, setLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState(null);
  const [syncNotification, setSyncNotification] = useState(false);

  // Auth state
  const [authToken, setAuthToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthChecked(true); return; }
    setAxiosAuthHeader(token);
    axios.post(`${API}/auth/validate`, { token })
      .then(res => {
        if (res.data.valid) {
          setAuthToken(token);
          setIsAuthenticated(true);
          if (res.data.user) {
            setCurrentUser(res.data.user);
            localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
          }
        } else {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setAxiosAuthHeader(null);
          setAuthToken(null);
          setCurrentUser(null);
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setAxiosAuthHeader(null);
        setAuthToken(null);
        setCurrentUser(null);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { token, user } = res.data;
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setAxiosAuthHeader(token);
    setAuthToken(token);
    setCurrentUser(user);
    setIsAuthenticated(true);
    return { token, user };
  }, []);

  const logout = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      try { await axios.post(`${API}/auth/logout`, { token }); } catch {}
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setAxiosAuthHeader(null);
    setAuthToken(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, []);

  const dismissChangePassword = useCallback(() => setShowChangePassword(false), []);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accounts`);
      setAccounts(res.data);
      return res.data;
    } catch (e) {
      console.error('Failed to load accounts', e);
      return [];
    }
  }, []);

  const refreshUnreadCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/unread_counts`);
      setUnreadCounts(res.data);
    } catch (e) {
      console.error('Failed to refresh counts', e);
    }
  }, []);

  const loadCategoryCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategoryCount(res.data);
    } catch (e) {
      console.error('Failed to load category counts', e);
    }
  }, []);

  const setCategoryEmail = useCallback(async (accountId, folder, uid, category) => {
    await axios.patch(`${API}/emails/${accountId}/${folder}/${uid}/category`, { category });
    await loadCategoryCount();
  }, [loadCategoryCount]);

  const loadCustomCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/categories/custom`);
      setCustomCategories(res.data);
    } catch (e) {
      console.error('Failed to load custom categories', e);
    }
  }, []);

  const createCategory = useCallback(async (data) => {
    const res = await axios.post(`${API}/categories/custom`, data);
    await loadCustomCategories();
    return res.data;
  }, [loadCustomCategories]);

  const deleteCategory = useCallback(async (id) => {
    await axios.delete(`${API}/categories/custom/${id}`);
    await loadCustomCategories();
  }, [loadCustomCategories]);

  const updateCategoryColor = useCallback(async (id, color, bgColor) => {
    await axios.patch(`${API}/categories/${id}/color`, { color, bg_color: bgColor });
    await loadCustomCategories();
  }, [loadCustomCategories]);

  const loadRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rules`);
      setRules(res.data);
    } catch (e) {
      console.error('Failed to load rules', e);
    }
  }, []);

  const createRule = useCallback(async (data) => {
    const res = await axios.post(`${API}/rules`, data);
    await loadRules();
    return res.data;
  }, [loadRules]);

  const updateRule = useCallback(async (id, data) => {
    await axios.put(`${API}/rules/${id}`, data);
    await loadRules();
  }, [loadRules]);

  const deleteRule = useCallback(async (id) => {
    await axios.delete(`${API}/rules/${id}`);
    await loadRules();
  }, [loadRules]);

  const addAccount = useCallback(async (data) => {
    const res = await axios.post(`${API}/accounts`, data);
    await loadAccounts();
    await refreshUnreadCounts();
    return res.data;
  }, [loadAccounts, refreshUnreadCounts]);

  const deleteAccount = useCallback(async (accountId) => {
    await axios.delete(`${API}/accounts/${accountId}`);
    await loadAccounts();
    await refreshUnreadCounts();
  }, [loadAccounts, refreshUnreadCounts]);

  const fetchEmails = useCallback(async (accountId, folder, forceSync = false) => {
    const res = await axios.get(`${API}/emails/${accountId}/${folder}`, {
      params: { force_sync: forceSync }
    });
    return res.data;
  }, []);

  const fetchEmailDetail = useCallback(async (accountId, folder, uid) => {
    const res = await axios.get(`${API}/emails/${accountId}/${folder}/${uid}`);
    // After fetching, refresh unread counts
    refreshUnreadCounts();
    return res.data;
  }, [refreshUnreadCounts]);

  const markEmailRead = useCallback(async (accountId, folder, uid, isRead) => {
    await axios.patch(`${API}/emails/${accountId}/${folder}/${uid}/flags`, { is_read: isRead });
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  const moveEmail = useCallback(async (accountId, folder, uid, targetFolder) => {
    await axios.post(`${API}/emails/${accountId}/${folder}/${uid}/move`, { target_folder: targetFolder });
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  const deleteEmail = useCallback(async (accountId, folder, uid) => {
    await axios.delete(`${API}/emails/${accountId}/${folder}/${uid}`);
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  const sendEmail = useCallback(async (data) => {
    await axios.post(`${API}/emails/send`, data);
    // Mark that user has replied (shows rubrica in sidebar)
    localStorage.setItem('mariani_has_replied', 'true');
    setHasReplied(true);
  }, []);

  const saveDraft = useCallback(async (data) => {
    await axios.post(`${API}/emails/draft`, data);
  }, []);

  const openCompose = useCallback((data = null) => {
    setComposeData(data);
    setComposeOpen(true);
  }, []);

  const closeCompose = useCallback(() => {
    setComposeOpen(false);
    setComposeData(null);
  }, []);

  const notifySyncDone = useCallback(() => setSyncNotification(true), []);
  const dismissSyncNotif = useCallback(() => setSyncNotification(false), []);

  const syncAccount = useCallback(async (accountId) => {
    await axios.get(`${API}/sync/${accountId}`);
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  // Auto-refresh unread counts every 60 seconds
  useEffect(() => {
    const init = async () => {
      const loaded = await loadAccounts();
      await refreshUnreadCounts();
      await loadCategoryCount();
      await loadCustomCategories();
      await loadRules();
      // Check if user has ever sent an email (shows rubrica)
      if (!localStorage.getItem('mariani_has_replied')) {
        try {
          const res = await axios.get(`${API}/has-sent`);
          if (res.data.has_sent) {
            localStorage.setItem('mariani_has_replied', 'true');
            setHasReplied(true);
          }
        } catch {}
      }
      // Auto-seed demo if no accounts
      if (loaded.length === 0) {
        try {
          await axios.post(`${API}/demo/setup`);
          await loadAccounts();
          await refreshUnreadCounts();
          await loadCategoryCount();
          await loadCustomCategories();
        } catch (e) {
          console.error('Failed to auto-seed demo', e);
        }
      }
    };
    init();
    const interval = setInterval(() => {
      refreshUnreadCounts();
      loadCategoryCount();
    }, 60000);
    return () => clearInterval(interval);
  }, [loadAccounts, refreshUnreadCounts, loadCategoryCount, loadCustomCategories, loadRules]);

  return (
    <AppContext.Provider value={{
      accounts,
      unreadCounts,
      categoryCount,
      customCategories,
      rules,
      hasReplied,
      loading,
      setLoading,
      composeOpen,
      composeData,
      syncNotification,
      notifySyncDone,
      dismissSyncNotif,
      // Auth
      authToken,
      currentUser,
      isAdmin: !!currentUser?.is_admin,
      isAuthenticated,
      authChecked,
      showChangePassword,
      login,
      logout,
      dismissChangePassword,
      loadAccounts,
      refreshUnreadCounts,
      loadCategoryCount,
      loadCustomCategories,
      setCategoryEmail,
      createCategory,
      updateCategoryColor,
      deleteCategory,
      loadRules,
      createRule,
      updateRule,
      deleteRule,
      addAccount,
      deleteAccount,
      fetchEmails,
      fetchEmailDetail,
      markEmailRead,
      moveEmail,
      deleteEmail,
      sendEmail,
      saveDraft,
      openCompose,
      closeCompose,
      syncAccount,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
